import type { InventoryItem } from '../../redux/inventorySlice';

/**
 * How long the import banner stays up. After that the rows are still marked
 * unreviewed, but the pantry stops asking: an import the user chose to ignore
 * must not become a permanent chore.
 */
export const IMPORT_BANNER_DAYS = 3;

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export interface ImportBatch {
  importId: string;
  count: number;
  deliveryDate: string | null;
}

/**
 * The delivery worth pointing at right now, or null.
 *
 * `reviewed === undefined` counts as reviewed — every item added through the
 * app, and every row that predates the field, has no business in a banner.
 */
export function activeImportBatch(
  items: InventoryItem[],
  today: Date = new Date()
): ImportBatch | null {
  const pending = items.filter(i => i && i.reviewed === false && i.importId);
  if (pending.length === 0) return null;

  const newest = pending.reduce((a, b) =>
    (b.importedAt ?? '') > (a.importedAt ?? '') ? b : a
  );

  if (newest.importedAt) {
    // Calendar days, not elapsed hours: an import at 9am and one at 5pm on the
    // same day must disappear on the same day.
    const age = Math.round(
      (startOfDay(today).getTime() - startOfDay(new Date(newest.importedAt)).getTime()) / MS_PER_DAY
    );
    if (age > IMPORT_BANNER_DAYS) return null;
  }

  const batch = pending.filter(i => i.importId === newest.importId);

  return {
    importId: newest.importId!,
    count: batch.length,
    deliveryDate: newest.deliveryDate ?? null,
  };
}
